import { useContext, useMemo } from 'react'
import { useAuth } from './useAuth'
import { useAdminIo } from './useAdmin'
import { ImpersonationContext, type ImpersonationContextValue } from './impersonationContext'
import type { S3Io } from '../upload/s3'

export function useImpersonation(): ImpersonationContextValue {
  const ctx = useContext(ImpersonationContext)
  if (!ctx) throw new Error('useImpersonation must be used within an ImpersonationProvider')
  return ctx
}

// Standard way to build an S3Io for user-scoped data: the admin client (with
// cross-user access) while impersonating, otherwise the user's own client. The
// id still comes from getCurrentIdentityId(); only the credentials differ here.
export function useCurrentIo(): S3Io | null {
  const { state: authState } = useAuth()
  const adminIo = useAdminIo()
  const { isImpersonating } = useImpersonation()
  return useMemo(() => {
    if (isImpersonating) return adminIo
    if (authState.status === 'authenticated') {
      return {
        credentials: authState.credentials,
        region: import.meta.env.VITE_AWS_REGION,
      }
    }
    return null
  }, [isImpersonating, adminIo, authState])
}
